import { MouseEvent } from "react";

import { getCookie } from "cookies-next";
import get from "lodash/get";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import once from "lodash/once";
import without from "lodash/without";
import words from "lodash/words";

import { ListingData } from "~/context/ListingPageDataContext";
import {
  IGuestRequestQuoteChild,
  IGuestRequestQuoteMaster,
  IProject,
} from "~/types/api-types";
import {
  AiAnswersEventProperties,
  BaseNormalizedListing,
  DialogInteractionProperties,
  ExtendedNormalizedListing,
  VendorShareProperties,
} from "~/types/local-types";
import {
  PSSK_COOKIE,
  PSSKT_COOKIE,
  PSSUT_COOKIE,
  PSMETRO_COOKIE,
  SEARCH_ID_COOKIE,
} from "~/utils/constants";

import {
  normalizeContractObj,
  segmentListingObj,
  parseFeeObj,
  normalizeBoardObj,
  buildDedupKey,
  buildAiAnswersProperties,
} from "./analytic-utils";
import { normalizeListingObj } from "./normalize-listing";

const analyticsIntegrations = {
  normalizeListingObj,
  normalizeContractObj,
  segmentListingObj,
  parseFeeObj,
  normalizeBoardObj,
  trackPage: (name?: string, properties?: Record<string, unknown>) => {
    window.analytics?.page(name, properties);
  },
  whichModalOpened: (whichModal: string) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.page(whichModal, {
      title: whichModal,
      url: window.location.toString(),
      path: window.location.hash,
      referrer: document.referrer,
      internalRef: window.location.search,
      internalReferrer: document.referrer,
    });
  },
  shareModalView: (
    data: BaseNormalizedListing | VendorShareProperties,
    origin: string,
    isVendor: boolean,
  ) => {
    if (!window.analytics) {
      return;
    }

    let listingCategory;
    if (!isVendor) {
      const useType = data.use_type || "";
      const metro = data.metro || "";
      const neighborhood = data.neighborhood || "";
      listingCategory = useType + "/" + metro + "/" + neighborhood;
    }
    const properties = {
      ...data,
      origin,
      category: isVendor ? data.category : listingCategory,
    };

    window.analytics.track("share modal viewed", properties);
  },
  trackEvent: (
    event: string,
    properties: Record<string, unknown> | Record<string, string>,
  ) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track(event, properties);
  },
  pageUrl: () => {
    if (!window.analytics) {
      return;
    }
    const locationPath = location.pathname;
    const page = locationPath.replace("/app/", "").replace("/", "");
    return page;
  },
  startBooking: (
    listing: ExtendedNormalizedListing,
    origin: string,
    additionalProps?: Record<string, unknown>,
  ) => {
    if (!window.analytics) {
      return;
    }

    const properties = {
      ...listing,
      neighborhood: listing.neighborhood,
      city: listing.city,
      state: listing.state,
      country: listing.country,
      zip: listing.zip,
      latitude: listing.latitude,
      longitude: listing.longitude,
      ownerFirstName: listing.ownerFirstName,
      ownerLastName: listing.ownerLastName,
      ownerAvatarUrl: listing.ownerAvatarUrl,
      ownerAvatarId: listing.ownerAvatarId,
      prices: listing.prices,
      host_responsiveness: listing.host_responsiveness,
      event_details: listing.event_details,
      parking_info: listing.parking_info,
      canonical_loc_data: listing.canonical_loc_data,
      currency: listing.currency,
      images: listing.images,
      cover_img: listing.cover_img,
      user_info: listing.user_info,
      category: listing.listing_title + "/" + listing.metro,
      origin: origin,
      listing_viewed_id: btoa(Date.now().toString()),
    };

    window.analytics.track(
      "start booking",
      Object.assign({}, properties, additionalProps),
    );
  },
  shareBtnClick: (
    data: BaseNormalizedListing | VendorShareProperties,
    origin: string,
    whichShareBtn: string,
  ) => {
    if (!window.analytics) {
      return;
    }

    const isVendor = "vendor_id" in data;
    let listingCategory;
    if (!isVendor) {
      const useType = data.use_type;
      const metro = data.metro || "unknown";
      const neighborhood = data.neighborhood || "unknown";
      listingCategory = useType + "/" + metro + "/" + neighborhood;
    }

    const properties = {
      ...data,
      origin,
      label: whichShareBtn,
      category: isVendor ? data.category : listingCategory,
      listing_viewed_id: btoa(Date.now().toString()),
    };

    window.analytics.track("share button clicked", properties);
  },
  boardJoinViewed: (board_id: string) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("join_modal_viewed", { board_id: board_id });
  },
  createBoardViewed: (
    listing: BaseNormalizedListing,
    isFirstTime: boolean,
    origin: string,
    activity: string,
    useType: string,
  ) => {
    if (!window.analytics) {
      return;
    }

    const properties = {
      ...listing,
      is_first_time: isFirstTime,
      origin,
      label: origin,
      activity,
      category: useType + "/" + listing.metro,
    };

    window.analytics.track("board form viewed", properties);
  },
  listBoardViewed: (
    listing: BaseNormalizedListing,
    isFirstTime: boolean,
    origin: string,
    activity: string,
    useType: string,
    boardsCount: number,
  ) => {
    if (!window.analytics) {
      return;
    }

    const properties = {
      ...listing,
      is_first_time: isFirstTime,
      origin,
      label: origin,
      activity,
      category: useType + "/" + listing.metro,
      boards_viewed: boardsCount,
    };

    window.analytics.track("board list viewed", properties);
  },
  duplicateName: (boardName: string) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("duplicate board error", { boardName });
  },
  shareBoardViewed: (
    project: Partial<IProject>,
    origin: string,
    activity: string,
    emailOrigin: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      origin: origin,
      label: origin,
      activity: activity,
      email_origin: emailOrigin,
      board_id: project._id,
      board_obj: project,
    };

    window.analytics.track("board invite form viewed", properties);
  },
  shareInviteSent: (
    project: Partial<IProject>,
    origin: string,
    activity: string,
    inviteCount: number,
    isDefaultMsg: boolean,
    emailOrigin: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      origin: origin,
      label: origin,
      email_origin: emailOrigin,
      activity: activity,
      email_count: inviteCount,
      is_default_message: isDefaultMsg,
      board_id: project._id,
      board_obj: project,
    };
    window.analytics.track("board Invite Sent (frontend)", properties);
  },
  sectionImpression: (
    pageLocation: string,
    sectionName: string,
    variant?: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      section_location: pageLocation,
      section_name: sectionName,
      variant: variant || "",
    };
    window.analytics.track("section viewed", properties);
  },
  listingCarouselViewed: (
    listingId: string,
    source: string,
    variant: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      listing_id: listingId,
      source: source || "recommended-listings",
      variant: variant,
    };
    window.analytics.track("listing carousel viewed", properties);
  },
  listingCarouselImpression: (
    recommendedFrom: string,
    recommendedTo: string,
    source: string,
    orderInCarousel: number,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      recommended_from: recommendedFrom,
      recommended_to: recommendedTo,
      source: source,
      order_in_listing_carousel: orderInCarousel,
    };
    window.analytics.track("Viewed Product Recommendation", properties);
  },
  photoCarouselInteraction: (listingId: string, source: string) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      listing_id: listingId,
      source: source || "listing header",
      search_id: (getCookie(SEARCH_ID_COOKIE) as string) ?? "none",
    };
    window.analytics.track("carousel viewed", properties);
  },
  showMinHoursWarning: (show: boolean) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("display min hours warning", { show });
  },
  availabilityBlock: (
    originalTimesRequested: Record<string, unknown>,
    useType: string,
    metro: string,
    conflictReason: string,
  ) => {
    // if no conflict, function shouldn't be called
    if (!window.analytics) {
      return;
    }

    const properties = originalTimesRequested;
    const category = useType + "/" + metro;

    properties.category = category;
    properties.reason_blocked = conflictReason; // add the reason for the conflict here
    window.analytics.track("availability blocked", properties);
  },
  bookingPriceCreated: (
    feeObj: Record<string, unknown>,
    contracts: [IGuestRequestQuoteMaster, IGuestRequestQuoteChild],
    useType: string,
    metro: string,
    isFullServiceVenue: boolean,
  ) => {
    if (!window.analytics) {
      return;
    }
    const pageName = analyticsIntegrations.pageUrl();
    const properties = feeObj;
    const category = useType + "/" + metro;
    properties.category = category;
    properties.label = `/${pageName}`;
    properties.search_keyword = getCookie(PSSK_COOKIE) as string;
    properties.search_keyword_type = getCookie(PSSKT_COOKIE) as string;
    properties.listing_viewed_id = btoa(Date.now().toString());
    properties.is_full_service_venue = isFullServiceVenue;
    // use new booking obj
    const mergedProperties = {
      ...properties,
      ...normalizeContractObj(contracts, "", []),
    };
    window.analytics.track("booking price created", mergedProperties);
  },
  boardCreated: (
    normalizedListing: BaseNormalizedListing,
    isFirstTime: boolean,
    origin: string,
    activity: string,
    useType: string,
    project: IProject,
  ) => {
    if (!window.analytics) {
      return;
    }
    const normalizedProject = analyticsIntegrations.normalizeBoardObj(project);

    const properties = {
      is_first_time: isFirstTime,
      origin: origin,
      label: origin,
      activity: activity,
      category: useType + "/" + normalizedListing.metro,
      board_event_date: project.created,
      metro: normalizedListing.metro,
    };

    const fullPayload = {
      ...properties,
      ...normalizedListing,
      ...normalizedProject,
    };

    window.analytics.track("board created (frontend)", fullPayload);
  },
  boardListingsSaved: (
    listing: BaseNormalizedListing,
    origin: string,
    activity: string,
    useType: string,
    listingImgID: string,
    isCreate: boolean,
    boardIds: string[],
    analyticName?: string,
  ) => {
    if (!window.analytics) {
      return;
    }

    const eventName = analyticName || "listing saved (frontend)";
    const properties = {
      ...listing,
      origin,
      label: origin,
      activity,
      listing_img_id: listingImgID,
      use_type: useType,
      category: useType + "/" + listing.metro,
      on_board_created: isCreate,
      boards_selected: boardIds.length,
      board_ids: boardIds,
      ...(eventName === "listing saved (frontend)"
        ? {
            dedup_key: buildDedupKey({
              eventType: "Listing Engagement",
              eventDetail: "save a listing to a board",
              id: listing.listing_id,
            }),
          }
        : {}),
    };

    window.analytics.track(eventName, properties);
  },
  transactableViewed: (missingArray: string[]) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      blockers: missingArray,
      category: "Account",
      label: missingArray,
      "page name": analyticsIntegrations.pageUrl(),
    };
    window.analytics.track("transactable blocker viewed", properties);
  },
  signupForm: (
    signupAction: string,
    extraAttrs: Record<string, unknown>,
    profile?: Record<string, unknown>,
    passedProperties?: Record<string, unknown>,
  ) => {
    /* extraAttrs: source, method */
    let actionSource: string | undefined;
    const urlSource = extraAttrs.source;

    if (!window.analytics) {
      return;
    }
    function getAction() {
      // sets actionSource also;
      let action: string;
      switch (urlSource) {
        case "fav":
          action = "save";
          actionSource = "details";
          break;
        case "favc":
          action = "save";
          actionSource = "carousel";
          break;
        case "favb":
          action = "save";
          actionSource = "bottom_bar";
          break;
        case "favt":
          action = "save";
          actionSource = "thumbnail";
          break;
        case "favg":
          action = "save";
          actionSource = "gallery";
          break;
        case "favm":
          action = "save";
          actionSource = "map thumbnail";
          break;
        case "fee":
          action = "start_bkg";
          actionSource = "details";
          break;
        case "feec":
          action = "start_bkg";
          actionSource = "carousel";
          break;
        case "msg":
          action = "msg_host";
          actionSource = "details";
          break;
        case "host":
          action = "list_space";
          actionSource = "hero";
          break;
        case "hostf":
          action = "list_space";
          actionSource = "footer";
          break;
        case "log":
          action = "log_in";
          break;
        case "nav":
          action = "sign_up";
          break;
        default:
          action = "none";
          actionSource = "none";
      }
      return action;
    }
    const action = getAction();

    const getPageType = function getPageType() {
      const splitPath = words(location.pathname);
      const parsedPath = !splitPath[0]
        ? "home"
        : "pages" === splitPath[0]
          ? splitPath[1]
          : splitPath[0];
      let pageType: string;
      switch (parsedPath) {
        case "s":
          pageType = "search";
          break;
        case "u":
          pageType = "use type";
          break;
        case "featured":
          pageType = "collections";
          break;
        case "plan":
          pageType = "activity";
          break;
        case "venues":
          pageType = "keyword";
          break;
        case "listings":
          pageType = "listing";
          break;
        default:
          pageType = parsedPath; /* home, host, signin, bkc, etc. */
      }
      return pageType;
    };

    const page_type = getPageType();
    const use_type = (getCookie(PSSUT_COOKIE) as string) || "none";
    const metro = (getCookie(PSMETRO_COOKIE) as string) || "none";
    const activity = (getCookie(PSSK_COOKIE) as string) || "none";
    const path = location.pathname;
    const app = "web";
    const category = [metro, use_type, activity].join("/");

    const isLoginOrSignup = "sign_up" === action || "log_in" === action;
    const isAccountCreated = "account created" === signupAction;
    const source = without(
      [action, isLoginOrSignup ? "nav" : page_type, actionSource],
      undefined,
    )
      .join(" ")
      .trim();
    const email_opt_out = get(profile, "email_unsubscribes.all", false);

    const attrs = {
      page_type: page_type,
      metro: metro,
      activity: activity,
      path: path,
      app: app,
      source: source,
      label: source,
      category: category,
      use_type: use_type,
      action: action,
      ...(isAccountCreated && { email_opt_out: email_opt_out }),
      ...passedProperties,
    };

    const attrsToSubmit: {
      action: string;
      activity: string;
      app: string;
      category: string;
      label: string;
      metro: string;
      page_type: string;
      path: string;
      referral_code?: string;
      referral_type?: string;
      referral_user?: string;
      source: string;
      use_type: string;
      context?: {
        traits: {
          id: string;
          email: string;
          createdAt: string;
          is_logged_in: boolean;
        };
      };
    } = extraAttrs.method ? merge(attrs, { method: extraAttrs.method }) : attrs;

    const actions = ["account created", "signup profile form submitted"];
    if (isEmpty(profile) || !includes(actions, signupAction)) {
      return window.analytics.track(signupAction, attrsToSubmit);
    }

    const ssoId = profile.SSOid as string;
    const identifyProps = {
      id: ssoId,
      email: profile.username as string,
      createdAt: profile.date_created as string,
      is_logged_in: true,
    };

    if ("account created" === signupAction) {
      attrsToSubmit.context = {
        traits: identifyProps,
      };
      window.analytics.alias(ssoId);
      window.analytics.identify(ssoId, identifyProps);
      window.analytics.track("account created", attrsToSubmit);
    } else if ("signup profile form submitted" === signupAction) {
      const properties = {
        heard_about_us: profile.heard_about_us,
        user_id: profile._id,
        timestamp: Date.now(),
      };

      window.analytics.identify(ssoId, {
        ...identifyProps,
        heard_about_us: profile.heard_about_us,
      });
      window.analytics.track("signup profile form submitted", {
        ...properties,
        ...attrsToSubmit,
      });
    }
  },
  trackLinkClick: (whichLink: string) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("link clicked", {
      category: "Account",
      label: whichLink,
      url: window.location.toString(),
      selection: whichLink,
    });
  },
  socialAccountLogin: (method: string, label: string, ssoID: string) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      app: "web",
      ssoID: ssoID,
      category: "Account",
      is_logged_in: true,
      method: method,
      label: label,
    };
    window.analytics.identify(ssoID, properties);
    window.analytics.track("account login", properties);
  },
  accountLogin: (ssoId: string) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      ssoID: ssoId,
      category: "Account",
      is_logged_in: true,
      method: "email",
      label: "email",
      app: "web",
    };
    window.analytics.identify(ssoId, properties);
    window.analytics.track("account login", properties);
  },
  photoViewed: ({
    listingId,
    imgId,
    type,
    externalId,
  }: {
    listingId: string;
    imgId: string;
    type: string;
    externalId: string;
  }) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      listing_id: listingId,
      img_id: imgId,
      source: "carousel",
      type,
      external_id: externalId,
    };
    window.analytics.track("listing image viewed", properties);
  },
  browseGalleryPhotoViewed: ({
    listingId,
    imgId,
    type,
    externalId,
  }: {
    listingId: string;
    imgId: string;
    type: string;
    externalId: string;
  }) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      listing_id: listingId,
      img_id: imgId,
      source: "carousel",
      type,
      external_id: externalId,
    };
    window.analytics.track("Browse Listing Gallery Image", properties);
  },
  calendarSync: (
    listingId: string,
    isFullServiceVenue: boolean,
    lastUpdated: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      listing_id: listingId,
      last_updated: lastUpdated,
      is_calendar_synced: !!lastUpdated,
      is_full_service_venue: isFullServiceVenue,
    };
    window.analytics.track("price quote calendar viewed", properties);
  },
  searchFormViewed: (location: string, activity: string) => {
    if (!window.analytics) {
      return;
    }
    const searchId = getCookie(SEARCH_ID_COOKIE) as string;
    const properties = {
      location,
      activity,
      search_id: searchId,
    };
    window.analytics.track("search form viewed", properties);
  },
  formSubmit: (formName: string) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("form attempt", { "form name": formName });
  },
  /**
   * Fires the Switch Growth "download the app" event behind the App Store /
   * Google Play links. The `dedup_key` lets GTM match this click against the
   * server-side install event, so one install isn't counted twice.
   */
  appDownloadClicked: () => {
    if (!window.analytics) {
      return;
    }

    const anonymousId = (getCookie("ajs_anonymous_id") as string) || "";
    window.analytics.track("download the app", {
      dedup_key: buildDedupKey({
        eventType: "App install",
        eventDetail: "download the app",
        id: anonymousId,
      }),
    });
  },
  itemClicked: (e?: MouseEvent<HTMLElement>, elementId?: string) => {
    if (!window.analytics) {
      return;
    }

    const element = e?.currentTarget;
    const elementName = elementId?.split("_").at(-1) || "";

    if (elementId === "header_link_referrals") {
      window.analytics.track("referral clicked", {
        button: "referral",
        source: "nav",
        destination: "referrals",
      });
    }

    const properties = {
      "page name": "SEO listing",
      selection: elementName,
      "click target": "",
      label: elementName,
      area: elementId,
      category: "undefined/undefined",
    };

    window.analytics.track("nav clicked", properties);

    if (element instanceof HTMLAnchorElement && element.href) {
      if (element.href.includes("#")) {
        window.location.href = element.href;
      } else {
        setTimeout(() => {
          window.location.href = element.href;
        }, 300);
      }
    }
  },
  listingsProductView: (
    listingObject: ListingData,
    originAction?: string,
    recommendedFrom?: string,
    carouselOrder?: number,
  ) => {
    if (!window.analytics) {
      return;
    }
    let origin, slashCount;

    if (originAction) {
      origin = originAction;
    } else if (-1 !== document.referrer.indexOf("/s/")) {
      origin = "search";
    } else if (-1 !== document.referrer.indexOf("/u/")) {
      origin = "use type";
    } else if (-1 !== document.referrer.indexOf("/venues/")) {
      slashCount = document.referrer.split("/").length;
      origin = 5 < slashCount ? "keyword" : "city";
    } else if (-1 !== document.referrer.indexOf("/featured/")) {
      origin = "collection";
    } else {
      origin = document.referrer;
    }

    const useType = listingObject.useType;
    const metro = listingObject.metro || "unknown";
    const neighborhood = listingObject.neighborhood || "unknown";
    const category = useType + "/" + metro + "/" + neighborhood;
    const searchKW = getCookie(PSSK_COOKIE) as string;
    const normalizedListing = normalizeListingObj(listingObject);
    const properties = {
      ...normalizedListing,
      date_created: listingObject.dateCreated,
      date_modified: listingObject.dateModified,
      search_keyword: searchKW,
      search_keyword_type: getCookie(PSSKT_COOKIE) as string,
      sku: listingObject.listingId,
      content_ids: listingObject.listingId,
      origin: origin,
      category: category,
      num_of_images: listingObject.images?.length || 0,
      nonInteraction: 1,
      city: listingObject.listingCity,
      country: listingObject.listingCountry,
      state: listingObject.listingState,
      search_id: getCookie(SEARCH_ID_COOKIE) as string,
      listing_viewed_id: btoa(Date.now().toString()),
      content_type: "destination",
      carousel_order: carouselOrder,
      recommended_from: recommendedFrom,
      is_full_service_venue: !!listingObject.fullService?.active,
      region: get(
        listingObject,
        "canonicalLocData.administrative_area_level_1.long_name",
        "",
      ),
      vendors_loaded: !!listingObject.vendors?.length,
    };

    window.analytics.track("Viewed Product", properties, {
      Quantcast: {
        labels: [searchKW, useType],
      },
    });
  },
  accountLogout: () => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      category: "Account",
    };
    window.analytics.track("account logout", properties);
    window.analytics.reset();
  },
  experimentLoaded: (
    experimentName: string,
    experimentVariant: string,
    useType?: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
      ...(useType ? { use_type: useType } : {}),
    };
    window.analytics.track("experiment loaded", properties);
  },
  vendorSectionExpanded: (vendorId: string, section: string) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      vendor_id: vendorId,
      section,
    };
    window.analytics.track("vendor section expanded", properties);
  },
  listingVendorClicked: (
    listingId: string,
    vendorId: string,
    vendorCategory: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      listing_id: listingId,
      vendor_id: vendorId,
      source: "listing page",
      vendor_category: vendorCategory,
    };
    window.analytics.track("listing vendor clicked", properties);
  },
  vendorInquiryFormViewed: (
    pageName,
    category,
    label,
    city,
    vendorId,
    vendorName,
    source,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      page_name: pageName,
      category,
      label,
      city,
      vendor_id: vendorId,
      vendor_name: vendorName,
      currency: "USD",
      source,
    };
    window.analytics.track("vendor inquiry form viewed", properties);
  },
  trackCreateSpaceLinkClickEvent: (
    source: "hero_section" | "cta_banner_section",
    linkElement: HTMLElement,
    ssoId?: string,
  ) => {
    if (
      typeof window === "undefined" ||
      typeof window.analytics === "undefined"
    ) {
      return;
    }

    // This is not a track call itself.
    // This adds a listener to navigation of a particular link
    // So segment can make sure event is tracked before navigating
    window.analytics?.trackLink?.(linkElement, "host list space clicked", {
      source,
      ...(document?.referrer ? { origin: document.referrer } : {}),
      ...(ssoId ? { sso_id: ssoId } : {}),
    });
  },
  // Same event as `trackCreateSpaceLinkClickEvent`, for CTAs that don't navigate
  // (e.g. the ones that open the host interstitial modal).
  trackCreateSpaceClickEvent: (
    source: "hero_section" | "cta_banner_section",
    ssoId?: string,
  ) => {
    if (
      typeof window === "undefined" ||
      typeof window.analytics === "undefined"
    ) {
      return;
    }

    window.analytics.track("host list space clicked", {
      source,
      ...(document?.referrer ? { origin: document.referrer } : {}),
      ...(ssoId ? { sso_id: ssoId } : {}),
    });
  },
  /**
   * The generic "dialog interaction" event, shared by every modal and tooltip.
   * Pass `onTracked` when the interaction navigates away (e.g. a submit that
   * follows a link): it runs once the event is sent, so the navigation can't
   * cancel it.
   */
  dialogInteraction: (
    properties: DialogInteractionProperties,
    onTracked?: () => void,
  ) => {
    if (!window.analytics) {
      onTracked?.();
      return;
    }
    if (!onTracked) {
      window.analytics.track("dialog interaction", properties);
      return;
    }
    // Makes sure onTracked is not called twice, when timeout
    // and callback are done in nearly the same time.
    const proceed = once(onTracked);
    setTimeout(proceed, 500);
    window.analytics.track("dialog interaction", properties, {}, proceed);
  },
  /**
   * The generic "dialog interaction" event, shared by every modal and tooltip.
   * Pass `onTracked` when the interaction navigates away (e.g. a submit that
   * follows a link): it runs once the event is sent, so the navigation can't
   * cancel it.
   */
  dialogInteraction: (
    properties: DialogInteractionProperties,
    onTracked?: () => void,
  ) => {
    if (!window.analytics) {
      onTracked?.();
      return;
    }
    if (!onTracked) {
      window.analytics.track("dialog interaction", properties);
      return;
    }
    // Makes sure onTracked is not called twice, when timeout
    // and callback are done in nearly the same time.
    const proceed = once(onTracked);
    setTimeout(proceed, 500);
    window.analytics.track("dialog interaction", properties, {}, proceed);
  },
  /**
   * A popular-keyword tab selection. This and `popularKeywordCityClicked` below are both
   * `link_clicked`; the reports tell them apart by `label`. Payloads are the old page's.
   */
  popularKeywordClicked: ({
    keyword,
    source,
  }: {
    keyword: string;
    source: string;
  }) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("link_clicked", {
      keyword,
      timestamp: Date.now(),
      source,
      label: "popular keyword",
    });
  },
  /** A click on one of the metros listed under the selected popular keyword. */
  popularKeywordCityClicked: ({
    city,
    keyword,
    source,
    url,
  }: {
    city: string;
    keyword: string;
    source: string;
    url: string;
  }) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track("link_clicked", {
      keyword,
      city,
      url,
      timestamp: Date.now(),
      source,
      label: "keywords city",
    });
  },
  /** The "Message host" pill inside the AI instant-answers modal. */
  aiMessageHostCta: (
    properties: AiAnswersEventProperties,
    onTracked: () => void,
  ) => {
    if (!window.analytics) {
      onTracked();
      return;
    }
    // Makes sure onTracked is not called twice, when timeout
    // and callback are done in nearly the same time.
    const proceed = once(onTracked);
    setTimeout(proceed, 500);
    window.analytics.track(
      "ai_message_host_cta",
      buildAiAnswersProperties(properties),
      {},
      proceed,
    );
  },
  /** The "Check availability" pill inside the AI instant-answers modal. */
  aiMessageCheckAvailability: (properties: AiAnswersEventProperties) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track(
      "ai_message_check_availability",
      buildAiAnswersProperties(properties),
    );
  },
  /** The guest dismissing the AI instant-answers modal. */
  aiMessageDismiss: (properties: AiAnswersEventProperties) => {
    if (!window.analytics) {
      return;
    }
    window.analytics.track(
      "ai_message_dismiss",
      buildAiAnswersProperties(properties),
    );
  },
  buttonClicked: (
    buttonName: string,
    buttonLocation: string,
    useType?: string,
  ) => {
    if (!window.analytics) {
      return;
    }
    const properties = {
      button_name: buttonName,
      button_location: buttonLocation,
      ...(useType ? { use_type: useType } : {}),
    };
    window.analytics.track("button clicked", properties);
  },
};

export default analyticsIntegrations;
